import { callFunction } from "./functions-client";

// Typed calls to the `jobs` edge function group (the hiring side). The job board
// carries a comprehensive, real-world trucking-job field set.

export const HIRING_TYPES = ["company", "recruiter", "broker_dispatcher"];

// Option lists — kept in sync with the server-side enums in jobs/logic.ts.
export const EMPLOYMENT_TYPE_OPTIONS: [string, string][] = [
  ["company_driver", "Company driver"],
  ["owner_operator", "Owner-operator"],
  ["lease", "Lease purchase"],
  ["other", "Other"],
];
export const ROUTE_TYPE_OPTIONS: [string, string][] = [
  ["otr", "OTR (over-the-road)"],
  ["regional", "Regional"],
  ["local", "Local"],
  ["dedicated", "Dedicated"],
];
export const HOME_TIME_OPTIONS: [string, string][] = [
  ["daily", "Home daily"],
  ["weekly", "Home weekly"],
  ["biweekly", "Every 2 weeks"],
  ["monthly", "Monthly"],
  ["varies", "Varies"],
];
export const PAY_TYPE_OPTIONS: [string, string][] = [
  ["annual", "Annual salary ($/yr)"],
  ["cpm", "Cents per mile (¢/mi)"],
  ["hourly", "Hourly ($/hr)"],
  ["percentage", "Percentage (%)"],
];
export const CDL_CLASS_OPTIONS: [string, string][] = [
  ["A", "Class A"],
  ["B", "Class B"],
  ["C", "Class C"],
];
export const ENDORSEMENT_OPTIONS = ["hazmat", "tanker", "doubles_triples", "twic", "passenger", "school_bus"];
export const BENEFIT_OPTIONS = ["health", "dental", "vision", "401k", "pto", "paid_orientation", "pet_friendly", "rider_program"];
export const JOB_EQUIPMENT_OPTIONS = ["dry van", "reefer", "flatbed", "step deck", "tanker", "car hauler", "box truck", "power only"];
export const FREIGHT_OPTIONS = ["general", "refrigerated", "hazmat", "oversized", "auto", "intermodal", "tanker", "livestock"];

export interface JobSummary {
  id: string;
  title: string;
  location: string | null;
  pay_min: number | null;
  pay_max: number | null;
  pay_type: string | null;
  employment_type: string | null;
  equipment: string[];
  route_type: string | null;
  home_time: string | null;
  cdl_class: string | null;
  sign_on_bonus: number | null;
  created_at: string;
  poster_name: string | null;
  poster_type: string;
  distance_mi?: number | null;
}

export interface JobDetail extends JobSummary {
  description: string | null;
  endorsements: string[];
  experience_months: number | null;
  freight_types: string[];
  benefits: string[];
  avg_miles_week: number | null;
  openings: number | null;
  status: string;
  poster_profile_id: string;
}

export interface MyJob {
  id: string;
  title: string;
  location: string | null;
  pay_min: number | null;
  pay_max: number | null;
  pay_type: string | null;
  employment_type: string | null;
  equipment: string[];
  route_type: string | null;
  cdl_class: string | null;
  status: string;
  created_at: string;
  application_count: number;
}

export interface JobApplication {
  id: string;
  status: string;
  cover_note: string | null;
  created_at: string;
  applicant_profile_id?: string;
  display_name?: string | null;
  headline?: string | null;
  job_id?: string;
  title?: string;
  location?: string | null;
  job_status?: string;
  poster_name?: string | null;
}

export interface CreateJobInput {
  poster_profile_id: string;
  org_id?: string | null;
  title: string;
  description?: string;
  location?: string;
  pay_min?: number;
  pay_max?: number;
  pay_type?: string;
  employment_type?: string;
  equipment?: string[];
  route_type?: string;
  home_time?: string;
  sign_on_bonus?: number;
  cdl_class?: string;
  endorsements?: string[];
  experience_months?: number;
  freight_types?: string[];
  benefits?: string[];
  avg_miles_week?: number;
  openings?: number;
}

function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v);
  return entries.length ? "?" + new URLSearchParams(entries as [string, string][]).toString() : "";
}

export const listJobs = (
  { radius, ...rest }: { q?: string; location?: string; equipment?: string; route_type?: string; cdl_class?: string; radius?: number } = {},
) => callFunction<{ jobs: JobSummary[]; geo?: boolean }>(`jobs/list${qs({ ...rest, radius: radius != null ? String(radius) : undefined })}`, { method: "GET" });

export const getJob = (id: string) =>
  callFunction<{ job: JobDetail }>(`jobs/get${qs({ id })}`, { method: "GET" });

export const createJob = (input: CreateJobInput) =>
  callFunction<{ job: { id: string } }>("jobs/create", { body: input });

export const myJobs = (profileId: string) =>
  callFunction<{ jobs: MyJob[] }>(`jobs/mine${qs({ profile_id: profileId })}`, { method: "GET" });

export const setJobStatus = (id: string, status: "open" | "closed") =>
  callFunction<{ job: { id: string; status: string } }>("jobs/close", { body: { id, status } });

export const applyToJob = (job_id: string, applicant_profile_id: string, cover_note?: string) =>
  callFunction<{ application: { id: string; status: string } }>("jobs/apply", {
    body: { job_id, applicant_profile_id, cover_note },
  });

export const jobApplications = (jobId: string) =>
  callFunction<{ applications: JobApplication[] }>(`jobs/applications${qs({ job_id: jobId })}`, { method: "GET" });

export const myApplications = (profileId: string) =>
  callFunction<{ applications: JobApplication[] }>(`jobs/my-applications${qs({ profile_id: profileId })}`, {
    method: "GET",
  });

export const setApplicationStatus = (application_id: string, status: string) =>
  callFunction<{ ok: boolean }>("jobs/set-status", { body: { application_id, status } });

/** Pay label that respects pay_type. Values are in the type's natural unit:
 *  annual → $/yr, cpm → cents/mi, hourly → $/hr, percentage → %. */
export function payLabel(min: number | null, max: number | null, payType?: string | null): string | null {
  const has = (n: number | null): n is number => n != null && n > 0;
  if (!has(min) && !has(max)) return null;
  const fmt = (n: number): string => {
    switch (payType) {
      case "cpm":
        return `$${(n / 100).toFixed(2)}/mi`;
      case "hourly":
        return `$${n}/hr`;
      case "percentage":
        return `${n}%`;
      default:
        return n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${n}`;
    }
  };
  if (has(min) && has(max)) return `${fmt(min)}–${fmt(max)}`;
  if (has(min)) return `${fmt(min)}+`;
  return `up to ${fmt(max as number)}`;
}

/** "$5,000 sign-on" or null. */
export function bonusLabel(bonus: number | null): string | null {
  return bonus ? `$${bonus.toLocaleString()} sign-on` : null;
}

/** "2+ yrs exp", "6 mo exp", or null. */
export function experienceLabel(months: number | null): string | null {
  if (!months) return null;
  if (months >= 12) return `${Math.floor(months / 12)}+ yr${months >= 24 ? "s" : ""} exp`;
  return `${months} mo exp`;
}
